/**
 * Routes that are part of the auth flow. Used to prevent redirect loops
 * (e.g. never set redirectTo to one of these paths).
 */
export const AUTH_PATHS: string[] = [
  "/login",
  "/signup",
  "/refresh-token",
  "/no-access",
  "/get-access",
  "/get-added",
  "/forgot-password",
  "/choose-product",
  "/auth/action",
]

/**
 * Docs live under a secret prefix `/<key>/docs/...` (plus the legacy `/docs`
 * path). They are public: loaders soft-auth them instead of requiring a session.
 */
export const isDocsPath = (pathname: string): boolean =>
  pathname.startsWith("/docs") || /^\/[^/]+\/docs(\/|$)/.test(pathname)

/**
 * Where a visitor without a usable session should land when they asked for
 * `target`: the console root maps to the public docs, a docs URL to itself
 * (query string kept), anything else needs a login (null).
 */
export const publicLandingFor = (target: string): string | null => {
  const pathname = target.split(/[?#]/)[0]
  if (pathname === "/") return "/docs"
  if (isDocsPath(pathname)) return target
  return null
}

export const DEFAULT_PAGE_SIZE = "20"

/**
 * Cookie name for storing advanced search accordion state (expanded/collapsed)
 */
export const ADVANCED_SEARCH_ACCORDION_COOKIE_NAME = "advanced-search-accordion:state"

/**
 * Cookie max age for advanced search accordion state (7 days in seconds)
 */
export const ADVANCED_SEARCH_ACCORDION_COOKIE_MAX_AGE = 60 * 60 * 24 * 7

/**
 * Maximum length for advanced search query in URL parameters.
 * Queries exceeding this length will be sent via POST instead of GET.
 */
export const MAX_QUERY_LENGTH_FOR_URL = 2000

const wktExample = `id;display_name;geometry
1;GeoJSON Polygon Example;"{ ""type"": ""Polygon"", ""coordinates"": [ [ [-74.0207362621559, 40.7612703139603], [-74.0207513348614, 40.7613910893867], [-74.0207763899338, 40.7615109096336], [-74.0208113284924, 40.7616293018254], [-74.0208560126506, 40.761745798722], [-74.0209102660604, 40.7618599405636], [-74.0209738746084, 40.7619712768843], [-74.0210465872607, 40.7620793682906], [-74.0211281170538, 40.7621837881952], [-74.0212181422267, 40.7622841245002], [-74.0213163074912, 40.7623799812243], [-74.021422225434, 40.7624709800646], [-74.0215354780452, 40.7625567618903], [-74.0216556183685, 40.7626369881598], [-74.0217821722649, 40.7627113422567], [-74.0219146402842, 40.7627795307392], [-74.0220524996354, 40.7628412844986], [-74.0221952062508, 40.762896359821], [-74.0223421969325, 40.7629445393492], [-74.0224928915754, 40.7629856329408], [-74.0226466954566, 40.763019478418], [-74.0228030015825, 40.7630459422082], [-74.0229611930843, 40.763064919871], [-74.0231206456523, 40.7630763365101], [-74.02328073, 40.7630801470693], [-74.0234408143477, 40.7630763365101], [-74.0236002669157, 40.763064919871], [-74.0237584584174, 40.7630459422082], [-74.0239147645434, 40.763019478418], [-74.0240685684246, 40.7629856329408], [-74.0242192630675, 40.7629445393492], [-74.0243662537492, 40.762896359821], [-74.0245089603646, 40.7628412844986], [-74.0246468197158, 40.7627795307392], [-74.024779287735, 40.7627113422567], [-74.0249058416315, 40.7626369881598], [-74.0250259819548, 40.7625567618903], [-74.025139234566, 40.7624709800646], [-74.0252451525087, 40.7623799812243], [-74.0253433177733, 40.7622841245002], [-74.0254333429462, 40.7621837881952], [-74.0255148727393, 40.7620793682906], [-74.0255875853916, 40.7619712768843], [-74.0256511939396, 40.7618599405636], [-74.0257054473494, 40.761745798722], [-74.0257501315076, 40.7616293018254], [-74.0257850700662, 40.7615109096336], [-74.0258101251386, 40.7613910893867], [-74.0258251978441, 40.7612703139603], [-74.0258302286974, 40.76114906], [-74.0258251978441, 40.7610278060397], [-74.0258101251386, 40.7609070306133], [-74.0257850700662, 40.7607872103664], [-74.0257501315076, 40.7606688181746], [-74.0257054473494, 40.760552321278], [-74.0256511939396, 40.7604381794364], [-74.0255875853916, 40.7603268431157], [-74.0255148727393, 40.7602187517094], [-74.0254333429462, 40.7601143318048], [-74.0253433177733, 40.7600139954998], [-74.0252451525087, 40.7599181387757], [-74.025139234566, 40.7598271399354], [-74.0250259819548, 40.7597413581097], [-74.0249058416315, 40.7596611318402], [-74.024779287735, 40.7595867777433], [-74.0246468197158, 40.7595185892608], [-74.0245089603646, 40.7594568355014], [-74.0243662537492, 40.759401760179], [-74.0242192630675, 40.7593535806508], [-74.0240685684246, 40.7593124870592], [-74.0239147645434, 40.759278641582], [-74.0237584584174, 40.7592521777918], [-74.0236002669157, 40.759233200129], [-74.0234408143477, 40.7592217834899], [-74.02328073, 40.7592179729307], [-74.0231206456523, 40.7592217834899], [-74.0229611930843, 40.759233200129], [-74.0228030015825, 40.7592521777918], [-74.0226466954566, 40.759278641582], [-74.0224928915754, 40.7593124870592], [-74.0223421969325, 40.7593535806508], [-74.0221952062508, 40.759401760179], [-74.0220524996354, 40.7594568355014], [-74.0219146402842, 40.7595185892608], [-74.0217821722649, 40.7595867777433], [-74.0216556183685, 40.7596611318402], [-74.0215354780452, 40.7597413581097], [-74.021422225434, 40.7598271399354], [-74.0213163074912, 40.7599181387757], [-74.0212181422267, 40.7600139954998], [-74.0211281170538, 40.7601143318048], [-74.0210465872607, 40.7602187517094], [-74.0209738746084, 40.7603268431157], [-74.0209102660604, 40.7604381794364], [-74.0208560126506, 40.760552321278], [-74.0208113284924, 40.7606688181746], [-74.0207763899338, 40.7607872103664], [-74.0207513348614, 40.7609070306133], [-74.0207362621559, 40.7610278060397], [-74.0207312313026, 40.76114906], [-74.0207362621559, 40.7612703139603] ] ] }
2;GeoJSON WKT Example;POLYGON ((14.45067098476278 48.05438226119631, 14.447602537649459 48.04931181642681, 14.449716118353024 48.04850135960132, 14.449108389468819 48.04765247765525, 14.452391413302763 48.04679178739025, 14.455159453006047 48.05133893821068, 14.453990009875668 48.0516186394274, 14.454333332628948 48.052199552485376, 14.452230480761898 48.052895205178714, 14.452713278384351 48.0538777089524, 14.45067098476278 48.05438226119631))`

const latlonradExample = `id;display_name;lat;lon;radius
10;Lat Lon Radius Example;43.6348831;-113.3003931;20`

const addressComponentsExample = `id;display_name;street_address;country;city;admin_level
20;Address Example;123 Broadway;USA;New York;NY`

const fullExample = `id;display_name;category;category_type;brand;brand_type;opening_hours;notes;includes_parking;lat;lon;radius;street_address;city;country;admin_level;admin_level_type;postal_code;postal_code_type;polygon
30;Full Example;327910;naics_2017;ffb244a1-7677-4708-ba5a-d765b9ebfa4d;brand;"{""Monday"":[""6:30 - 17:30""],""Tuesday"":[""6:30 - 17:30""],""Wednesday"":[""6:30 - 17:30""],""Thursday"":[""6:30 - 17:30""],""Friday"":[""6:30 - 17:30""],""Saturday"":[],""Sunday"":[""Closed""]}";Here is an example note about this location;false;48.051778969312835;14.453878906743824;20;Hinterbergerstraße 2;Steyr;Austria;Upper Austria;freeform;4400;freeform;POLYGON ((14.45067098476278 48.05438226119631, 14.447602537649459 48.04931181642681, 14.449716118353024 48.04850135960132, 14.449108389468819 48.04765247765525, 14.452391413302763 48.04679178739025, 14.455159453006047 48.05133893821068, 14.453990009875668 48.0516186394274, 14.454333332628948 48.052199552485376, 14.452230480761898 48.052895205178714, 14.452713278384351 48.0538777089524, 14.45067098476278 48.05438226119631))`

export const CSV_EXAMPLES = {
  wkt: wktExample,
  latlonrad: latlonradExample,
  addressComponents: addressComponentsExample,
  full: fullExample,
}

export const GEOJSON_EXAMPLE = JSON.stringify(
  {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: {
          id: "10001",
          display_name: "GeoJson Example",
          category: "451110",
          category_type: "naics_us_2022",
          brand: "Chevron",
          brand_type: "some_brand_type",
          includes_parking: true,
          opening_hours:
            '{"Monday":["6:30 - 17:30"],"Tuesday":["6:30 - 17:30"],"Wednesday":["6:30 - 17:30"],"Thursday":["6:30 - 17:30"],"Friday":["6:30 - 17:30"],"Saturday":["6:30 - 17:30"],"Sunday":["6:30 - 17:30"]}',
          notes: "Some notes",
          lat: 35.95231127900852,
          lon: -83.89309823513031,
          radius: 100,
          street_address: "123 Main St",
          country: "USA",
          city: "Knoxville",
          postal_code: "37902",
          postal_code_type: "some_postal_code_type",
          admin_level: "Tennessee",
          admin_level_type: "us_state",
        },
        geometry: {
          type: "Polygon",
          coordinates: [
            [
              [-83.89309823513031, 35.95231127900852],
              [-83.89310628175735, 35.95221465769536],
              [-83.89296546578407, 35.95222117149547],
              [-83.89296144247055, 35.952304765215835],
              [-83.89309823513031, 35.95231127900852],
            ],
          ],
        },
      },
    ],
  },
  null,
  2,
)

export const INTENTS = {
  EDIT_COLLECTION: "editCollection",
  EXPORT_COLLECTION: "exportCollection",
  DELETE_COLLECTION: "deleteCollection",
  CREATE_BOUNDARY: "createBoundary",
  EDIT_BOUNDARY: "editBoundary",
  DELETE_BOUNDARY: "deleteBoundary",
  DELETE_BOUNDARIES: "deleteBoundaries",
  IMPORT_GCS_BOUNDARY: "importGcsBoundary",
  LIST_FAILED: "listFailedValidations",
  LIST_DUPLICATE_GROUPS: "listDuplicateGroups",
  PUBLISH_VALIDATION: "publishValidation",
  GET_VALIDATION_STATUS: "getValidationStatus",
  CANCEL_VALIDATION: "cancelValidation",
  RESTORE_VALIDATION: "restoreValidation",
  PUBLISH_VERSION: "publishVersion",
  CREATE_NEW_VERSION: "createNewVersion",
  ARCHIVE_VERSION: "archiveVersion",
  UNARCHIVE_VERSION: "unarchiveVersion",
  DELETE_DRAFT: "deleteDraft",
} as const

export const VALIDATION_SECTIONS = {
  REQUIRED: "required",
  TYPESETS: "typesets",
  GEOMETRY: "geometry",
} as const

export const TYPESET_GROUPS = {
  brand_type: "brands",
  category_type: "categories",
  admin_level_type: "admin_levels",
  postal_code_type: "postal_codes",
} as const

export const REQUIRED_FIELDS = [
  { id: "client_provided_id", label: "ID" },
  { id: "display_name", label: "Display Name" },
  { id: "street_address", label: "Street Address" },
  { id: "postal_code", label: "Postal Code" },
  { id: "city", label: "City" },
  { id: "country", label: "Country" },
  { id: "admin_level", label: "Admin Level" },
] as const

export const TYPESET_FIELDS = [
  { field: "brands", id: "brand_type", label: "Brand", group: "brands" },
  {
    field: "categories",
    id: "category_type",
    label: "Category",
    group: "categories",
  },
  {
    field: "admin_levels",
    id: "admin_level_type",
    label: "Admin Level",
    group: "admin_levels",
  },
  {
    field: "postal_codes",
    id: "postal_code_type",
    label: "Postal Code",
    group: "postal_codes",
  },
] as const

export const GEOMETRY_FIELDS = [
  // { id: 'excludeSensitive', label: 'Exclude sensitive' },
  // { id: 'notOverWater', label: 'Not over water' },
  { id: "no_overlapping_lines", label: "No overlapping lines" },
  { id: "no_multi_polygons", label: "No multi polygons" },
  {
    id: "minimum_area",
    label: "Minimum area",
    hasInput: true,
    inputType: "number",
    unit: "m²",
  },
  {
    id: "maximum_area",
    label: "Maximum area",
    hasInput: true,
    inputType: "number",
    unit: "m²",
  },
  {
    id: "container_polygon",
    label: "Contained by polygon",
    hasTextarea: true,
    placeholder: "Paste your GEOJSON or WKT container",
  },
] as const

export const DUPLICATES_FIELDS = [
  { id: "unique_ids", label: "Unique IDs" },
  { id: "unique_polygons", label: "Unique Polygons" },
  { id: "unique_addresses", label: "Unique Addresses" },
] as const

export const FAILURE_TYPES = {
  FIELD: "FAILURE_TYPE_FIELD_VALIDATION",
  GEOMETRY: "FAILURE_TYPE_GEOMETRY_VALIDATION",
  DUPLICATES: "FAILURE_TYPE_DUPLICATES_VALIDATION",
  UNKNOWN: "FAILURE_TYPE_UNSPECIFIED",
} as const

export type ValidationSection = keyof typeof VALIDATION_SECTIONS
export type TypesetField =
  | "brands"
  | "categories"
  | "admin_levels"
  | "postal_codes"
export type TypesetFieldKey = keyof typeof TYPESET_GROUPS

export const BOUNDARY_POPUP_ACCORDION_COOKIE_NAME = "boundary-popup:accordion-state"
export const BOUNDARY_POPUP_ACCORDION_COOKIE_MAX_AGE = 60 * 60 * 24 * 7 // One week in seconds
